import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

function Spinner() {
  return <span style={styles.spinner} aria-hidden="true" />;
}

export default function OAuthAuthorizedPage() {
  const [searchParams] = useSearchParams();

  const view = useMemo(() => {
    const status = searchParams.get('status');
    const error = searchParams.get('error');
    const message = searchParams.get('message');

    if (status === 'success') {
      return {
        tone: 'success',
        title: 'Propel extension connected',
        text: 'Your browser session has been linked. Return to the side panel on Upwork, then close this tab.',
      };
    }

    if (status === 'error' || error) {
      return {
        tone: 'error',
        title: error === 'access_denied' ? 'Authorization canceled' : 'Connection failed',
        text:
          message
          || (error === 'access_denied'
            ? 'The extension was not connected. Close this tab and retry from the Propel side panel when you are ready.'
            : 'Propel could not complete the extension sign-in. Close this tab and retry from the side panel.'),
      };
    }

    return {
      tone: 'pending',
      title: 'Finishing your extension login',
      text: 'Propel is verifying the connection with your side panel. This usually takes a moment.',
    };
  }, [searchParams]);

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoMark}>P</div>

        <div style={styles.illustrationWrap}>
          {view.tone === 'pending' && <Spinner />}
          {view.tone === 'success' && <div style={styles.successGlyph}>✓</div>}
          {view.tone === 'error' && <div style={styles.errorGlyph}>!</div>}
        </div>

        <h1 style={styles.title}>{view.title}</h1>
        <p style={styles.text}>{view.text}</p>

        {view.tone === 'success' && (
          <p style={styles.helperText}>This tab stays open on purpose so you can confirm the handoff before closing it.</p>
        )}

        {view.tone === 'pending' && (
          <p style={styles.helperText}>If the side panel does not update after a few seconds, keep this tab open and retry from the extension.</p>
        )}

        {view.tone === 'error' && (
          <p style={styles.helperText}>No proposal or Upwork workflow state was changed.</p>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 16px',
    background: 'linear-gradient(180deg, #f6f3ec 0%, #f8f6f1 100%)',
    fontFamily: '"Segoe UI", Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    color: '#201f1d',
  },
  card: {
    width: '100%',
    maxWidth: '620px',
    padding: '40px 36px',
    borderRadius: '26px',
    background: '#fbf9f4',
    border: '1px solid rgba(38, 33, 28, 0.08)',
    boxShadow: '0 30px 80px rgba(68, 59, 46, 0.08)',
    textAlign: 'center',
  },
  logoMark: {
    width: '52px',
    height: '52px',
    margin: '0 auto 20px',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#4F46E5',
    color: '#ffffff',
    fontWeight: 800,
    fontSize: '24px',
    letterSpacing: '0.04em',
  },
  illustrationWrap: {
    minHeight: '72px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '10px',
  },
  title: {
    margin: '0 0 12px',
    fontSize: '36px',
    lineHeight: 1.1,
    fontWeight: 700,
    letterSpacing: '-0.03em',
  },
  text: {
    margin: '0 auto',
    maxWidth: '460px',
    fontSize: '18px',
    lineHeight: 1.55,
    color: '#57534e',
  },
  helperText: {
    margin: '22px auto 0',
    maxWidth: '460px',
    fontSize: '15px',
    lineHeight: 1.6,
    color: '#78716c',
  },
  successGlyph: {
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    background: '#dcfce7',
    color: '#15803d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: '26px',
  },
  errorGlyph: {
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    background: '#fee2e2',
    color: '#b91c1c',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: '26px',
  },
  spinner: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    border: '3px solid rgba(79, 70, 229, 0.18)',
    borderTopColor: '#4F46E5',
    animation: 'oauth-authorized-spin 0.8s linear infinite',
    display: 'inline-block',
  },
};

if (typeof document !== 'undefined' && !document.getElementById('oauth-authorized-spin-style')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'oauth-authorized-spin-style';
  styleEl.textContent = `
    @keyframes oauth-authorized-spin {
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleEl);
}
